import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await register(formData);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || "Une erreur est survenue lors de l'inscription");
      }
    } catch (err) {
      setError("Une erreur est survenue lors de l'inscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 32px 0 rgba(0,0,0,0.08)',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}
              >
                Créer un compte
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                  {error}
                </Alert>
              )}
              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="firstName"
                  label="Prénom"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'grey.400' },
                      '&:hover fieldset': { borderColor: 'grey.700' },
                      '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                    },
                    '& .MuiInputLabel-root': { color: 'grey.600' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                    '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="lastName"
                  label="Nom"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'grey.400' },
                      '&:hover fieldset': { borderColor: 'grey.700' },
                      '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                    },
                    '& .MuiInputLabel-root': { color: 'grey.600' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                    '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Adresse email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'grey.400' },
                      '&:hover fieldset': { borderColor: 'grey.700' },
                      '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                    },
                    '& .MuiInputLabel-root': { color: 'grey.600' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                    '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Mot de passe"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'grey.400' },
                      '&:hover fieldset': { borderColor: 'grey.700' },
                      '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                    },
                    '& .MuiInputLabel-root': { color: 'grey.600' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                    '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirmer le mot de passe"
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'grey.400' },
                      '&:hover fieldset': { borderColor: 'grey.700' },
                      '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                    },
                    '& .MuiInputLabel-root': { color: 'grey.600' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                    '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    borderRadius: '50px',
                    py: 1.5,
                    mb: 3,
                    fontSize: '1.1rem',
                    boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                    '&:hover': {
                      boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                    },
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "S'inscrire"}
                </Button>
              </Box>
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Vous avez déjà un compte ?{' '}
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => navigate('/login')}
                    sx={{ p: 0, minWidth: 'auto', ml: 0.5, boxShadow: 'none' }}
                  >
                    Se connecter
                  </Button>
                </Typography>
              </Box>
            </Paper>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default Register;
